import { useMutation, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../lib/axios";
import { useRef, useState } from "react";
import { toast } from "sonner";

const newIdempotencyKey = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random()
    .toString(36)
    .slice(2)}`;
};

const useAdd = (
  endpoint,
  onSuccess = null,
  customSuccessMessage = null,
  customErrorMessage = null
) => {
  const queryClient = useQueryClient();
  const [responseData, setResponseData] = useState(null);
  const [errorData, setErrorData] = useState(null);

  // Guards against duplicate submissions:
  // - inFlightRef blocks a second call while one is already running
  //   (double-click / repeated Ctrl+Enter before the button disables).
  // - idempotencyKeyRef is sent as Idempotency-Key. It is kept after a
  //   network failure (server may have saved but the reply was lost), so
  //   the retry is recognised as the same submission, and rotated only
  //   after a confirmed success so the next, new record gets a fresh key.
  const inFlightRef = useRef(null);
  const idempotencyKeyRef = useRef(null);

  const addMutation = useMutation({
    mutationFn: async (data) => {
      try {
        const url = endpoint.endsWith('/') ? endpoint : `${endpoint}/`;
        if (!idempotencyKeyRef.current) {
          idempotencyKeyRef.current = newIdempotencyKey();
        }
        const response = await axiosInstance.post(url, data, {
          headers: { "Idempotency-Key": idempotencyKeyRef.current },
        });

        setResponseData(response.data);
        setErrorData(null);
        return response.data;
      } catch (error) {
        const errorResponse = error.response?.data || { message: error.message };
        setErrorData(errorResponse);
        throw error;
      }
    },
    onSuccess: (data) => {
      // Confirmed saved: the next record is a new submission.
      idempotencyKeyRef.current = null;
      if (customSuccessMessage) {
        toast.success(customSuccessMessage);
      }
      queryClient.invalidateQueries({ queryKey: [endpoint] });
      if (typeof onSuccess === 'function') {
        onSuccess(data);
      }
    },
    onError: (err) => {
      // Server answered 409 (key already used with different data):
      // rotate so the user's next explicit save is a fresh submission.
      if (err.response?.status === 409) {
        idempotencyKeyRef.current = null;
      }
      const backendErrors = err.response?.data;

      if (backendErrors && typeof backendErrors === "object") {
        if (backendErrors.non_field_errors) {
          const messages = Array.isArray(backendErrors.non_field_errors)
            ? backendErrors.non_field_errors.join(", ")
            : backendErrors.non_field_errors;
          toast.error(messages);
          return;
        }

        Object.entries(backendErrors).forEach(([key, value]) => {
          const errorMessage = Array.isArray(value) ? value.join(", ") : value;
          const title = key === 'detail' ? '' : `${key.charAt(0).toUpperCase() + key.slice(1)}: `;
          toast.error(`${title}${errorMessage}`);
        });
      } else if (typeof backendErrors === 'string') {
        toast.error(backendErrors);
      } else if (customErrorMessage) {
        toast.error(customErrorMessage);
      }
    },
  });

  const handleAdd = (data) => {
    // A save is already running: don't send another request, just
    // hand back the in-flight promise.
    if (inFlightRef.current) {
      return inFlightRef.current;
    }
    setErrorData(null);
    const promise = addMutation.mutateAsync(data);
    inFlightRef.current = promise;
    const clear = () => {
      if (inFlightRef.current === promise) inFlightRef.current = null;
    };
    promise.then(clear, clear);
    return promise;
  };

  return {
    handleAdd,
    responseData,
    errorData,
    isLoading: addMutation.isPending,
    isError: addMutation.isError,
    isSuccess: addMutation.isSuccess,
    error: addMutation.error,
  };
};

export default useAdd;
